import { useTranslation } from 'react-i18next';

import type { AutocompleteRenderInputParams } from '@mui/material';

import { MultiSelect } from 'components/shared';
import { EntitySearchContainer } from 'components/mrp/components/EntitySearch/EntitySearch.styles';
import {
  EntitySearchIcon,
  EntitySearchTextField
} from 'components/mrp/components/EntitySearch/components/EntitySearchInput/EntitySearchInput.styles';

import { useMrpActivities } from '../../hooks/useMrpActivities';

import type { ActivityResponse } from 'generated/index';
import type { AppointmentsOverviewActivityFilterProps } from './AppointmentsOverviewActivityFilter.types';

/** As narrow as the field can be with its placeholder and a count. */
const MIN_WIDTH = 280;

/**
 * Which appointments: the activities, a field under the picker in the filter panel. It narrows what the
 * picker names (selection ∧ activity) and may be the only filter. A pick goes to the panel's draft, which
 * lists the picked activities as chips with the rest, so the field only counts them.
 *
 * Dressed like the picker's search — EntitySearch's card and field, a placeholder rather than a floating
 * label — so the two read as one group and their text lines sit at the same height.
 */
const AppointmentsOverviewActivityFilter = ({
  value,
  onChange
}: AppointmentsOverviewActivityFilterProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const activities = useMrpActivities();
  const pickedActivities = activities.filter(({ entityId }) =>
    value.includes(entityId)
  );

  const handleChange = (picked: ActivityResponse[]) =>
    onChange(picked.map(({ entityId }) => entityId));

  const title = t('appointmentsOverview.filters.activity');
  const label =
    pickedActivities.length > 0
      ? `${title} (${pickedActivities.length})`
      : title;

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <EntitySearchTextField
      {...params}
      placeholder={label}
      slotProps={{
        input: {
          ...params.InputProps,
          startAdornment: (
            <>
              <EntitySearchIcon type="clipboard-list" />
              {params.InputProps.startAdornment}
            </>
          )
        },
        htmlInput: { ...params.inputProps, 'aria-label': label }
      }}
    />
  );

  return (
    <EntitySearchContainer minWidth={MIN_WIDTH}>
      <MultiSelect<ActivityResponse>
        renderInput={renderInput}
        options={activities}
        selectedOptions={pickedActivities}
        onChange={handleChange}
        idProperty="entityId"
        optionLabelProp="name"
        disableClearable={false}
      />
    </EntitySearchContainer>
  );
};

export default AppointmentsOverviewActivityFilter;
